import { NIA_GAMES } from "./games";
import { collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import { getOrganisationDisplayName } from "./orgPresentation";
import { fetchOrgUsers } from "./orgUsers";
import {
  DEFAULT_BRAND_COLORS,
  NIA_BRAND_COLORS,
  NIA_THEME,
  getDefaultOrgTheme as getDefaultThemePair,
  normalizeThemePair,
  resolveOrgTheme,
} from "../theme/orgTheme";
import { DEFAULT_ORG_ID } from "./brandAssets";
import { getSessionRecencyTimestamp, sortSessionsByRecency } from "./sessionOrdering";

const GLOBAL_SESS_KEY = "nia_sessions";
const DAY_MS = 24 * 60 * 60 * 1000;
const ORG_PROFILE_KEY = "org_profile";
export const SAME_USER_CONTINUITY_QUERY_LIMIT = 50;
const NIA_BRANDED_ORG_IDS = new Set([DEFAULT_ORG_ID, "org_shuttle"]);
const resolveLogoDarkUrl = (profile = {}) =>
  profile.logoDarkUrl || profile.logoUrlDark || profile.darkLogoUrl || profile.logoDark || "";
const LEGACY_SHUTTLE_THEME = {
  primary: "#1f7a6e",
  accent: "#2b8e5f",
  bg: "#ffffff",
  surface: "#f7fbf9",
  surfaceAlt: "#edf5f1",
  text: "#333333",
  muted: "#5e6e67",
  border: "#d6e3dc",
  chart1: "#1f7a6e",
  chart2: "#7bc243",
  chart3: "#0e6a5c",
  chart4: "#2f9a6e",
  chart5: "#e5526b",
};

const NIA_ORG_ASSETS = Object.freeze({
  logoLight: "/brand/nia/logo-light.svg",
  logoDark: "/brand/nia/logo-dark.svg",
  wordmarkLight: "/brand/nia/wordmark-light.svg",
  wordmarkDark: "/brand/nia/wordmark-dark.svg",
  logoIconLight: "/icons/nia/logo-icon-light.svg",
  logoIconDark: "/icons/nia/logo-icon-dark.svg",
  splashLight: "/brand/nia/splash-light.png",
  splashDark: "/brand/nia/splash-dark.png",
  splashLightFallback: "/brand/nia/splash-light.svg",
  splashDarkFallback: "/brand/nia/splash-dark.svg",
  splashLandscapeLight: "/brand/nia/splash-landscape-light.png",
  splashLandscapeDark: "/brand/nia/splash-landscape-dark.png",
  splashLandscapeLightFallback: "/brand/nia/splash-landscape-light.svg",
  splashLandscapeDarkFallback: "/brand/nia/splash-landscape-dark.svg",
});

const sampleSessions = [
  {
    user: "Samira L.",
    dateISO: new Date(Date.now() - DAY_MS * 1).toISOString(),
    msgs: 18,
    preview: "Needed a reset before a deadline. Found calm in 6 minutes.",
    block: "Focus Drift",
    game: "Move-It",
  },
  {
    user: "Kai B.",
    dateISO: new Date(Date.now() - DAY_MS * 2).toISOString(),
    msgs: 11,
    preview: "Work stress spiked after a meeting; used Move-It game.",
    block: "Low Energy",
    game: "MaxIt",
  },
  {
    user: "Jules M.",
    dateISO: new Date(Date.now() - DAY_MS * 3).toISOString(),
    msgs: 22,
    preview: "Focus drifted during study, ran a brief grounding sequence.",
    block: "Focus Drift",
    game: "PingWins",
  },
  {
    user: "Priya K.",
    dateISO: new Date(Date.now() - DAY_MS * 4).toISOString(),
    msgs: 9,
    preview: "Morning check-in for anxiety. Breath pacing helped.",
    block: "Anxious Loop",
    game: "Two-Flags",
  },
  {
    user: "Leo S.",
    dateISO: new Date(Date.now() - DAY_MS * 6).toISOString(),
    msgs: 15,
    preview: "Energy dipped midday, quick reset before a workout.",
    block: "Low Energy",
    game: "ClimbUp",
  },
  {
    user: "Mina R.",
    dateISO: new Date(Date.now() - DAY_MS * 9).toISOString(),
    msgs: 13,
    preview: "Late night focus drift. Tried a short breath practice.",
    block: "Time Warp",
    game: "Blocker",
  },
];

function canUseStorage() {
  return typeof window !== "undefined" && window.localStorage;
}

function readStoredSessions(orgId) {
  if (!canUseStorage()) return [];
  try {
    const key = `${GLOBAL_SESS_KEY}__org_${orgId || "org_default"}`;
    const raw = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function toDateISO(value) {
  const date = new Date(value || Date.now());
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

function toMs(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function normalizeOrgSession(record = {}) {
  const createdAt = toMs(record.createdAt || record.startedAt || record.date || record.dateISO) || Date.now();
  const updatedAt = toMs(record.updatedAt || 0) || null;
  const lastMessageAt = toMs(record.lastMessageAt || 0) || null;
  const messages = Array.isArray(record.messages) ? record.messages : [];
  const preview =
    record.sessionSummary?.summaryText ||
    record.preview ||
    record.issueSummary ||
    record.summary ||
    record.advicePreview ||
    record.title ||
    "Session";
  return {
    ...record,
    id: record.id || record.sessionId || String(createdAt),
    createdAt,
    updatedAt,
    lastMessageAt,
    startedAt: toMs(record.startedAt || createdAt) || createdAt,
    endedAt: toMs(record.endedAt || 0) || null,
    user: record.user || record.client || record.name || record.userName || "Unknown",
    client: record.client || record.user || record.name || record.userName || "Unknown",
    dateISO: record.dateISO || toDateISO(createdAt),
    msgs: messages.length ? messages.length : Number(record.msgs || record.messageCount || 0),
    messageCount: Number(record.messageCount || record.msgs || messages.length || 0),
    messages,
    preview,
    issueSummary: record.issueSummary || preview,
    advicePreview: record.advicePreview || "",
    block: record.block || record.blockName || null,
    game: record.game || record.gameName || null,
    role: record.role || "member",
    userEmail: record.userEmail || "",
    followUp: record.followUp || null,
    followUpCreated: Boolean(record.followUpCreated || record.followUp),
    flagged: Boolean(record.flagged),
    rating: record.rating && typeof record.rating === "object"
      ? {
          ...record.rating,
          createdAt: toMs(record.rating.createdAt || record.rating.updatedAt || createdAt) || 0,
        }
      : null,
  };
}

function countBy(list, key) {
  return list.reduce((acc, item) => {
    const label = item[key];
    if (!label) return acc;
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
}

function toTopList(countMap) {
  return Object.entries(countMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);
}

function toShareList(countMap) {
  return Object.entries(countMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function computeOrgMetricsFromSessions(
  sessions = [],
  { planSeats = 30, pricePerSeat = 12, allowFallback = true } = {}
) {
  const normalized = sessions.map(normalizeOrgSession);
  const sessionsSource = normalized.length ? normalized : (allowFallback ? sampleSessions : []);

  const now = Date.now();
  const weekAgo = now - DAY_MS * 7;
  const monthAgo = now - DAY_MS * 30;
  const weekSessions = sessionsSource.filter(
    (session) => getSessionRecencyTimestamp(session) >= weekAgo
  );
  const monthSessions = sessionsSource.filter(
    (session) => getSessionRecencyTimestamp(session) >= monthAgo
  );

  const sessionsWeek = weekSessions.length;
  const sessionsMonth = monthSessions.length;
  const activeUsersWeek = new Set(weekSessions.map((session) => session.user)).size;
  const avgMsgsPerSession = sessionsWeek
    ? weekSessions.reduce((sum, session) => sum + session.msgs, 0) / sessionsWeek
    : 0;

  const seatsUsed = new Set(sessionsSource.map((session) => session.user)).size;
  const blockSource = sessionsSource.some((session) => session.block)
    ? sessionsSource
    : (allowFallback ? sampleSessions : []);
  const gameSource = sessionsSource.some((session) => session.game)
    ? sessionsSource
    : (allowFallback ? sampleSessions : []);
  const blockCounts = countBy(weekSessions.length ? weekSessions : blockSource, "block");
  const gameCounts = countBy(weekSessions.length ? weekSessions : gameSource, "game");
  const hasGameCounts = Object.keys(gameCounts).length > 0;
  const fallbackGameCounts = allowFallback
    ? NIA_GAMES.reduce((acc, name, index) => {
        acc[name] = Math.max(1, NIA_GAMES.length - index);
        return acc;
      }, {})
    : {};

  const topBlocks = toTopList(blockCounts);
  const gameShare = toShareList(hasGameCounts ? gameCounts : fallbackGameCounts);

  const monthlyCost = planSeats * pricePerSeat;

  return {
    kpis: {
      sessionsWeek,
      sessionsMonth,
      activeUsersWeek,
      avgMsgsPerSession,
      seatsUsed,
    },
    topBlocks,
    gameShare,
    recentSessions: [...sessionsSource]
      .sort((a, b) => getSessionRecencyTimestamp(b) - getSessionRecencyTimestamp(a))
      .slice(0, 5),
    seats: { used: seatsUsed, total: planSeats },
    billing: { monthlyCost, pricePerSeat },
  };
}

export function computeOrgMetrics({ planSeats = 30, pricePerSeat = 12, orgId = "org_default", allowFallback = true } = {}) {
  const storedSessions = allowFallback ? readStoredSessions(orgId).map(normalizeOrgSession) : [];
  return computeOrgMetricsFromSessions(storedSessions, { planSeats, pricePerSeat, allowFallback });
}

function toStoredProfile(orgId, profile = {}) {
  const brandColors = profile.brandColors || {
    primary: profile.primary || profile.theme?.light?.color?.primary || profile.theme?.primary || DEFAULT_BRAND_COLORS.primary,
    accent: profile.accent || profile.theme?.light?.color?.accent || profile.theme?.accent || DEFAULT_BRAND_COLORS.accent,
  };
  const payload = {
    name: profile.name || "Your Organisation",
    logoUrl: profile.logoUrl || "",
    logoDarkUrl: resolveLogoDarkUrl(profile),
    primary: brandColors.primary,
    accent: brandColors.accent,
  };
  if (NIA_BRANDED_ORG_IDS.has(orgId)) {
    Object.assign(payload, NIA_ORG_ASSETS);
  }
  if (profile.theme && profile.hasCustomTheme) {
    payload.theme = normalizeThemePair(profile.theme, brandColors);
  }
  return payload;
}

function normalizeOrgProfile(orgId, data = {}) {
  const resolved = resolveOrgTheme({ id: orgId, ...data });
  const normalized = {
    ...data,
    name: data.name || "",
    logoUrl: data.logoUrl || "",
    logoDarkUrl: resolveLogoDarkUrl(data),
    primary: resolved.brandColors.primary,
    accent: resolved.brandColors.accent,
    brandColors: resolved.brandColors,
    theme: resolved.theme,
    hasCustomTheme: resolved.hasCustomTheme,
    isGeneratedTheme: resolved.isGenerated,
  };
  if (NIA_BRANDED_ORG_IDS.has(orgId)) {
    return {
      ...normalized,
      ...NIA_ORG_ASSETS,
    };
  }
  return normalized;
}

export async function fetchOrgProfile(user, orgId = "org_default") {
  if (!user?.uid) return readOrgProfileLocal(orgId);
  try {
    const ref = doc(db, "orgs", orgId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data() || {};
      const profile = normalizeOrgProfile(orgId, data);
      const shouldWriteNiaTheme =
        NIA_BRANDED_ORG_IDS.has(orgId) &&
        (!data.theme?.light || !data.theme?.dark || data.primary !== NIA_BRAND_COLORS.primary || data.accent !== NIA_BRAND_COLORS.accent);
      if (shouldWriteNiaTheme && (user.role === "superadmin" || user.role === "admin")) {
        try {
          await setDoc(
            ref,
            {
              primary: NIA_BRAND_COLORS.primary,
              accent: NIA_BRAND_COLORS.accent,
              theme: NIA_THEME,
              ...NIA_ORG_ASSETS,
            },
            { merge: true }
          );
        } catch {}
      }
      writeOrgProfileLocal(orgId, profile);
      return profile;
    }
  } catch {}
  return readOrgProfileLocal(orgId);
}

export async function saveOrgProfile(user, orgId, profile) {
  if (!user?.uid || user?.role === "investor") return;
  const payload = toStoredProfile(orgId, profile);
  writeOrgProfileLocal(orgId, payload);
  try {
    const ref = doc(db, "orgs", orgId);
    await setDoc(ref, payload, { merge: true });
  } catch {}
}

export function validateAuthorizedOrganizations(value) {
  if (!Array.isArray(value)) {
    const error = new Error("Invalid authorised organisation response.");
    error.code = "data-loss";
    throw error;
  }

  const seen = new Set();
  return value.map((row) => {
    const id = String(row?.id || "").trim();
    if (!id || id.includes("/") || seen.has(id)) {
      const error = new Error("Invalid authorised organisation response.");
      error.code = "data-loss";
      throw error;
    }
    seen.add(id);
    return {
      id,
      name: getOrganisationDisplayName({ id, name: row?.name }),
      logoUrl: String(row?.logoUrl || "").trim(),
      archived: Boolean(row?.archived),
      type: String(row?.type || "pilot").trim() || "pilot",
      tags: Array.isArray(row?.tags)
        ? row.tags.map((tag) => String(tag || "").trim()).filter(Boolean)
        : [],
    };
  });
}

export function getSafeOrgLoadDiagnostic(error) {
  const rawCode = String(error?.code || "unknown")
    .trim()
    .toLowerCase()
    .replace(/^functions\//, "");
  const code = /^[a-z0-9_-]{1,48}$/.test(rawCode) ? rawCode : "unknown";
  return {
    operation: "getDocs(orgs)",
    code,
  };
}

export async function listOrgs(
  user,
  { throwOnError = false, queryOrganizations = null } = {}
) {
  if (!user?.uid || user?.role !== "superadmin") return [];
  try {
    const load = queryOrganizations || (async () => {
      const snapshot = await getDocs(collection(db, "orgs"));
      return snapshot.docs.map((document) => ({
        ...(document.data() || {}),
        id: document.id,
      }));
    });
    return validateAuthorizedOrganizations(await load());
  } catch (error) {
    if (throwOnError) throw error;
    return [];
  }
}

export async function listOrgsByIds(
  user,
  orgIds = [],
  { fetchOrganization = null } = {}
) {
  const role = String(user?.role || "").trim().toLowerCase();
  const canLoadMembershipOrganizations =
    role === "superadmin" ||
    role === "coach" ||
    role === "admin" ||
    role === "member" ||
    role === "investor";
  if (
    !user?.uid ||
    !canLoadMembershipOrganizations ||
    !Array.isArray(orgIds) ||
    !orgIds.length
  ) {
    return [];
  }

  const requestedIds = Array.from(new Set(
    orgIds
      .map((id) => String(id || "").trim())
      .filter((id) => id && !id.includes("/"))
  ));
  if (!requestedIds.length) return [];

  const load = fetchOrganization || (async (id) => {
    const snap = await getDoc(doc(db, "orgs", id));
    return snap.exists() ? (snap.data() || {}) : null;
  });
  const results = await Promise.allSettled(
    requestedIds.map(async (id) => {
      const data = await load(id);
      return data ? { ...data, id } : null;
    })
  );
  const fulfilledRows = results
    .filter((result) => result.status === "fulfilled" && result.value)
    .map((result) => result.value);
  const firstFailure = results.find((result) => result.status === "rejected");

  if (!fulfilledRows.length && firstFailure) {
    throw firstFailure.reason;
  }

  const rows = validateAuthorizedOrganizations(fulfilledRows);
  return role === "superadmin" ? rows : rows.filter((org) => !org.archived);
}

export const LIVE_SHUTTLEWORTH_ORG_ID = "org_shuttle";
export const SHUTTLEWORTH_RESERVE_ORG_ID = "org_shuttle_reserve";
export const LIVE_NIA_ORG_NAME = "NIA Coach";

export async function prepareShuttleworthReserveOrg(
  user,
  {
    sourceOrgId = LIVE_SHUTTLEWORTH_ORG_ID,
    reserveOrgId = SHUTTLEWORTH_RESERVE_ORG_ID,
    liveName = LIVE_NIA_ORG_NAME,
  } = {}
) {
  if (!user?.uid || user?.role !== "superadmin") {
    return { ok: false, reason: "forbidden" };
  }
  if (!sourceOrgId || !reserveOrgId) {
    return { ok: false, reason: "invalid_org_id" };
  }
  try {
    const sourceRef = doc(db, "orgs", sourceOrgId);
    const reserveRef = doc(db, "orgs", reserveOrgId);
    const [sourceSnap, reserveSnap] = await Promise.all([getDoc(sourceRef), getDoc(reserveRef)]);
    if (!sourceSnap.exists()) {
      return { ok: false, reason: "source_missing" };
    }
    if (reserveSnap.exists()) {
      return { ok: false, reason: "reserve_exists" };
    }

    const sourceData = sourceSnap.data() || {};
    const reserveName = `${sourceData.name || "Shuttleworth Academy"} (Reserve)`;
    const reserveTags = Array.from(new Set([...(Array.isArray(sourceData.tags) ? sourceData.tags : []), "reserve", "inactive"]));

    await setDoc(reserveRef, {
      ...sourceData,
      name: reserveName,
      archived: true,
      hidden: true,
      inactive: true,
      tags: reserveTags,
      reserveCopyOf: sourceOrgId,
      reserveCreatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await setDoc(
      sourceRef,
      {
        name: liveName,
        archived: false,
        hidden: false,
        inactive: false,
        primary: NIA_BRAND_COLORS.primary,
        accent: NIA_BRAND_COLORS.accent,
        theme: NIA_THEME,
        hasCustomTheme: true,
        logoUrl: NIA_ORG_ASSETS.logoLight,
        logoDarkUrl: NIA_ORG_ASSETS.logoDark,
        wordmarkLight: NIA_ORG_ASSETS.wordmarkLight,
        wordmarkDark: NIA_ORG_ASSETS.wordmarkDark,
        logoIconLight: NIA_ORG_ASSETS.logoIconLight,
        logoIconDark: NIA_ORG_ASSETS.logoIconDark,
        splashLight: NIA_ORG_ASSETS.splashLight,
        splashDark: NIA_ORG_ASSETS.splashDark,
        splashLightFallback: NIA_ORG_ASSETS.splashLightFallback,
        splashDarkFallback: NIA_ORG_ASSETS.splashDarkFallback,
        splashLandscapeLight: NIA_ORG_ASSETS.splashLandscapeLight,
        splashLandscapeDark: NIA_ORG_ASSETS.splashLandscapeDark,
        splashLandscapeLightFallback: NIA_ORG_ASSETS.splashLandscapeLightFallback,
        splashLandscapeDarkFallback: NIA_ORG_ASSETS.splashLandscapeDarkFallback,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return {
      ok: true,
      sourceOrgId,
      reserveOrgId,
      liveName,
      reserveName,
    };
  } catch {
    return { ok: false, reason: "write_failed" };
  }
}

export async function createOrg(user, orgId, name) {
  if (!user?.uid || user?.role !== "superadmin") return;
  if (!orgId) return;
  try {
    const ref = doc(db, "orgs", orgId);
    await setDoc(
      ref,
      {
        name: name || orgId,
        archived: false,
        type: "Pilot",
        planSeats: 30,
        pricePerSeat: 199,
        primary: DEFAULT_BRAND_COLORS.primary,
        accent: DEFAULT_BRAND_COLORS.accent,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch {}
}

export async function updateOrgMeta(user, orgId, updates = {}) {
  if (!user?.uid || user?.role !== "superadmin") return false;
  if (!orgId) return false;
  try {
    const ref = doc(db, "orgs", orgId);
    await setDoc(ref, updates, { merge: true });
    return true;
  } catch {
    return false;
  }
}

export async function createDemoOrg(user) {
  if (!user?.uid || user?.role !== "superadmin") return null;
  const id = `demo_${Date.now()}`;
  await createOrg(user, id, `Demo Org ${new Date().toLocaleDateString()}`);
  return id;
}

export async function fetchOrgSettings(user, orgId = "org_default") {
  if (!user?.uid) return {};
  try {
    const ref = doc(db, "orgs", orgId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      return snap.data() || {};
    }
  } catch {}
  return {};
}

export async function fetchOrgSeatSnapshot(user, orgId = "org_default", fallbackPlanSeats = 30) {
  const [settings, orgUsers] = await Promise.all([
    fetchOrgSettings(user, orgId),
    fetchOrgUsers(user, orgId),
  ]);

  const seatsTotal = Number(settings?.planSeats) > 0 ? Number(settings.planSeats) : Number(fallbackPlanSeats) || 30;
  const activeUsers = (orgUsers || []).filter(
    (u) => !u.disabled && String(u.status || "").toLowerCase() !== "pending"
  );
  const seatsUsed = activeUsers.filter(
    (u) => !String(u.role || "").toLowerCase().includes("superadmin")
  ).length;

  return { seatsUsed, seatsTotal };
}

export async function saveOrgSettings(user, orgId, settings) {
  if (!user?.uid || user?.role === "investor") return;
  try {
    const ref = doc(db, "orgs", orgId);
    await setDoc(ref, settings, { merge: true });
  } catch {}
}

export async function fetchOrgSessionById(user, orgId = "org_default", sessionId) {
  if (!user?.uid || !sessionId) return null;
  try {
    const ref = doc(db, "orgs", orgId, "sessions", String(sessionId));
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
  } catch {
    return null;
  }
}

export async function fetchOrgSessions(user, orgId = "org_default") {
  if (!user?.uid) return [];
  try {
    const base = collection(db, "orgs", orgId, "sessions");
    const q =
      user.role === "admin" || user.role === "coach" || user.role === "superadmin" || user.role === "investor"
        ? query(base, orderBy("createdAt", "desc"), limit(500))
        : query(base, where("userId", "==", user.uid), orderBy("createdAt", "desc"), limit(500));
    const snap = await getDocs(q);
    return sortSessionsByRecency(snap.docs.map((docSnap) => normalizeOrgSession({ id: docSnap.id, ...docSnap.data() })));
  } catch {
    return [];
  }
}

export async function fetchSameUserOrgSessions(user, orgId = "org_default", userId = user?.uid) {
  const callerUserId = String(user?.uid || "").trim();
  const safeOrgId = String(orgId || "").trim();
  const safeUserId = String(userId || "").trim();
  if (!callerUserId || !safeOrgId || !safeUserId || callerUserId !== safeUserId) return [];
  try {
    const base = collection(db, "orgs", safeOrgId, "sessions");
    const q = query(
      base,
      where("userId", "==", safeUserId),
      orderBy("createdAt", "desc"),
      limit(SAME_USER_CONTINUITY_QUERY_LIMIT)
    );
    const snap = await getDocs(q);
    return sortSessionsByRecency(
      snap.docs
        .map((docSnap) => normalizeOrgSession({ ...docSnap.data(), id: docSnap.id }))
        .filter((session) => (
          String(session?.orgId || "").trim() === safeOrgId &&
          String(session?.userId || "").trim() === safeUserId
        ))
    );
  } catch {
    return [];
  }
}

export async function fetchOrgSessionsFiltered(user, orgId = "org_default", { role, game, days } = {}) {
  if (!user?.uid) return [];
  const sessions = await fetchOrgSessions(user, orgId);
  const cutoff = days && Number(days) > 0
    ? Date.now() - Number(days) * 24 * 3600 * 1000
    : 0;
  return sessions
    .filter((session) => {
      if (role && role !== "all" && String(session.role || "member") !== String(role)) return false;
      if (game && game !== "all" && String(session.game || "") !== String(game)) return false;
      if (cutoff && getSessionRecencyTimestamp(session) < cutoff) return false;
      return true;
    })
    .sort((a, b) => getSessionRecencyTimestamp(b) - getSessionRecencyTimestamp(a))
    .slice(0, 500);
}

export async function exportSessionsCsvFiltered(user, orgId, filters = {}, filename = "nia-sessions-filtered.csv") {
  if (!user?.uid || user?.role !== "superadmin") return;
  const csv = await buildSessionsCsvFiltered(user, orgId, filters);
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

export async function buildSessionsCsvFiltered(user, orgId, filters = {}) {
  if (!user?.uid) return "";
  const sessions = await fetchOrgSessionsFiltered(user, orgId, filters);
  const needle = (filters.userSearch || "").toLowerCase().trim();
  const tag = (filters.tag || "").toLowerCase().trim();
  const filtered = needle
    ? sessions.filter((s) => {
        const hay = `${s.user || ""} ${s.client || ""} ${s.userEmail || ""}`.toLowerCase();
        return hay.includes(needle);
      })
    : sessions;
  const filteredByTag = tag
    ? filtered.filter((s) => Array.isArray(s.tags) && s.tags.map((t) => String(t).toLowerCase()).includes(tag))
    : filtered;
  return buildSessionsCsv(filteredByTag);
}

export async function pruneOrgSessions(user, orgId, keepDays = 90) {
  if (!user?.uid || user?.role !== "superadmin") return;
  try {
    const cutoff = Number(keepDays) > 0 ? Date.now() - Number(keepDays) * 24 * 3600 * 1000 : null;
    const base = collection(db, "orgs", orgId, "sessions");
    const q =
      cutoff === null
        ? query(base, limit(500))
        : query(base, orderBy("createdAt", "asc"), limit(500));
    const snap = await getDocs(q);
    const stale = snap.docs.filter((docSnap) => {
      if (cutoff === null) return true;
      const ts = docSnap.data()?.createdAt;
      return typeof ts === "number" ? ts < cutoff : false;
    });
    await Promise.all(stale.map((docSnap) => deleteDoc(docSnap.ref)));
  } catch {}
}

export async function countOrgSessionsOlderThan(user, orgId, keepDays = 90) {
  if (!user?.uid || user?.role !== "superadmin") return 0;
  if (!keepDays || Number(keepDays) <= 0) return 0;
  try {
    const cutoff = Date.now() - Number(keepDays) * 24 * 3600 * 1000;
    const base = collection(db, "orgs", orgId, "sessions");
    const q = query(base, orderBy("createdAt", "asc"), limit(500));
    const snap = await getDocs(q);
    return snap.docs.reduce((acc, docSnap) => {
      const ts = docSnap.data()?.createdAt;
      if (typeof ts === "number" && ts < cutoff) return acc + 1;
      return acc;
    }, 0);
  } catch {
    return 0;
  }
}

export async function computeOrgMetricsFromFirestore(user, orgId, opts = {}) {
  const seatSnapshot = await fetchOrgSeatSnapshot(user, orgId, opts?.planSeats || 30).catch(() => null);
  const resolvedPlanSeats = seatSnapshot?.seatsTotal || opts?.planSeats || 30;
  const sessions = await fetchOrgSessions(user, orgId);
  const baseMetrics = sessions.length
    ? computeOrgMetricsFromSessions(sessions, { ...opts, planSeats: resolvedPlanSeats })
    : computeOrgMetrics({ ...opts, orgId, planSeats: resolvedPlanSeats });

  if (seatSnapshot) {
    return {
      ...baseMetrics,
      kpis: {
        ...(baseMetrics.kpis || {}),
        seatsUsed: seatSnapshot.seatsUsed,
      },
      seats: {
        used: seatSnapshot.seatsUsed,
        total: seatSnapshot.seatsTotal,
      },
    };
  }

  if (sessions.length) {
    return computeOrgMetricsFromSessions(sessions, { ...opts, planSeats: resolvedPlanSeats });
  }
  return computeOrgMetrics({ ...opts, orgId, planSeats: resolvedPlanSeats });
}

function toCsvValue(value) {
  const str = value == null ? "" : String(value);
  const escaped = str.replace(/"/g, '""');
  return `"${escaped}"`;
}

export function buildInsightsCsv(rows = []) {
  const header = ["User", "Date", "Messages", "Preview", "Block", "Game"];
  const lines = rows.map((row) => [
    row.user || "",
    row.dateISO || "",
    row.msgs || 0,
    row.preview || "",
    row.block || "",
    row.game || "",
  ]);
  return [header, ...lines].map((r) => r.map(toCsvValue).join(",")).join("\n");
}

export function exportInsightsCsv(rows = [], filename = "nia-insights.csv") {
  const csv = buildInsightsCsv(rows);
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

export async function fetchOrgHealthSummaries(user) {
  if (!user?.uid || user?.role !== "superadmin") return [];
  try {
    const orgList = await listOrgs(user);
    const results = await Promise.all(
      orgList.map(async (org) => {
        const settings = await fetchOrgSettings(user, org.id);
        const planSeats = settings?.planSeats || 30;
        const sessions = await fetchOrgSessions(user, org.id);
        const metrics = sessions.length
          ? computeOrgMetricsFromSessions(sessions, { planSeats })
          : computeOrgMetrics({ planSeats });
        const now = Date.now();
        const monthAgo = now - DAY_MS * 30;
        const normalized = sessions.map(normalizeOrgSession);
        const sessions30 = normalized.filter((session) => new Date(session.dateISO).getTime() >= monthAgo).length;
        const lastActivity = sessions[0]?.createdAt || null;
        return {
          id: org.id,
          name: org.name,
          seatsUsed: metrics?.seats?.used || 0,
          seatsTotal: metrics?.seats?.total || planSeats,
          activeUsersWeek: metrics?.kpis?.activeUsersWeek || 0,
          sessions30,
          lastActivity,
        };
      })
    );
    return results;
  } catch {
    return [];
  }
}

export function buildSessionsCsv(sessions = []) {
  const header = ["User", "Date", "Messages", "Preview"];
  const rows = sessions.map((s) => [
    s.user || s.client || s.userEmail || "Unknown",
    new Date(s.createdAt || s.date || Date.now()).toLocaleString(),
    Array.isArray(s.messages) ? s.messages.length : s.msgs || 0,
    s.preview || s.issueSummary || s.summary || "",
  ]);
  const lines = [header, ...rows].map((row) => row.map(toCsvValue).join(","));
  return lines.join("\n");
}

export async function exportOrgSessionsCsv(user, orgId = "org_default") {
  if (!user?.uid || user?.role !== "superadmin") return;
  const sessions = await fetchOrgSessions(user, orgId);
  const csv = buildSessionsCsv(sessions);
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `nia-sessions-${orgId}.csv`;
  a.click();
}

export function getOrgStats({ planSeats = 30 } = {}) {
  const metrics = computeOrgMetrics({ planSeats });
  const seatsUsed = metrics.seats.used || 0;
  const seatsTotal = metrics.seats.total || planSeats;
  const memberCount = Math.max(seatsUsed - 3, 0);

  return {
    seatsUsed,
    seatsTotal,
    roles: {
      coach: 2,
      admin: 1,
      member: memberCount,
    },
  };
}

export function seedOrgDemo(orgId = "org_default") {
  if (!canUseStorage()) return;
  const payload = sampleSessions.map((session, index) => ({
    id: Date.now() + index,
    createdAt: new Date(session.dateISO).getTime(),
    client: session.user,
    user: session.user,
    title: session.preview.slice(0, 48),
    summary: session.preview,
    preview: session.preview,
    msgs: session.msgs,
    block: session.block,
    game: session.game,
    messages: [
      { role: "user", content: "Session starter" },
      { role: "assistant", content: "Supportive response" },
    ],
  }));
  const key = `${GLOBAL_SESS_KEY}__org_${orgId}`;
  window.localStorage.setItem(key, JSON.stringify(payload));
}

export function clearOrgDemo(orgId = "org_default") {
  if (!canUseStorage()) return;
  const key = `${GLOBAL_SESS_KEY}__org_${orgId}`;
  window.localStorage.removeItem(key);
}

export function clearLocalOrgCache(orgId = "org_default", userId = "") {
  if (!canUseStorage()) return;
  const targetOrg = orgId || "org_default";
  const orgSessionKey = `${GLOBAL_SESS_KEY}__org_${targetOrg}`;
  const orgProfileKey = `${ORG_PROFILE_KEY}__${targetOrg}`;
  try {
    window.localStorage.removeItem(orgSessionKey);
    window.localStorage.removeItem(GLOBAL_SESS_KEY); // legacy unscoped key
    window.localStorage.removeItem(orgProfileKey);
    window.localStorage.removeItem(ORG_PROFILE_KEY); // legacy key
    const keys = Object.keys(window.localStorage || {});
    keys.forEach((key) => {
      if (!key) return;
      if (key.includes(`__org_${targetOrg}`)) window.localStorage.removeItem(key);
      if (userId && key.startsWith(`nia_actions_${userId}`)) window.localStorage.removeItem(key);
      if (key.startsWith("nia_coach_read_")) window.localStorage.removeItem(key);
    });
    try {
      window.dispatchEvent(new Event("pref-changed"));
      window.dispatchEvent(new Event("nia-cache-cleared"));
    } catch {}
  } catch {}
}

export async function seedOrgSessionsToFirestore(user, orgId = "org_default") {
  if (!user?.uid || user?.role === "investor") return;
  try {
    const payload = sampleSessions.map((session, index) => ({
      id: Date.now() + index,
      createdAt: new Date(session.dateISO).getTime(),
      client: session.user,
      user: session.user,
      title: session.preview.slice(0, 48),
      summary: session.preview,
      preview: session.preview,
      msgs: session.msgs,
      block: session.block,
      game: session.game,
      orgId,
      userId: user.uid,
      userEmail: user.email || "",
      messages: [
        { role: "user", content: "Session starter" },
        { role: "assistant", content: "Supportive response" },
      ],
    }));
    await Promise.all(
      payload.map((record) => setDoc(doc(db, "orgs", orgId, "sessions", String(record.id)), record, { merge: true }))
    );
  } catch {}
}
function readOrgProfileLocal(orgId = "org_default") {
  if (!canUseStorage()) return {};
  try {
    const key = `${ORG_PROFILE_KEY}__${orgId}`;
    const data = JSON.parse(window.localStorage.getItem(key) || "{}");
    if (!data || typeof data !== "object") return {};
    return normalizeOrgProfile(orgId, data);
  } catch {
    return {};
  }
}

function writeOrgProfileLocal(orgId = "org_default", profile) {
  if (!canUseStorage()) return;
  try {
    const key = `${ORG_PROFILE_KEY}__${orgId}`;
    window.localStorage.setItem(key, JSON.stringify(profile));
  } catch {}
}

export function getDefaultOrgTheme() {
  return getDefaultThemePair();
}

export function getShuttleOrgTheme() {
  return normalizeThemePair(LEGACY_SHUTTLE_THEME, {
    primary: LEGACY_SHUTTLE_THEME.primary,
    accent: LEGACY_SHUTTLE_THEME.accent,
  });
}
